import { useEffect, useState } from 'react'
import { FileInput, Image, Stack, Text } from '@mantine/core'
import { IconUpload } from '@tabler/icons-react'
import { useMedplum } from '@medplum/react'
import { logError } from '@/utils/errorUtils'

interface FacilityLogoFieldProps {
  /** Currently selected (not-yet-saved) logo file, owned by the parent form. */
  value: File | null
  onChange: (file: File | null) => void
  /** FHIR reference (e.g. "Binary/123") of the already-saved logo, if any. */
  existingLogoRef?: string
}

/**
 * Logo upload field with an inline preview. Shows the newly selected file if one
 * is picked, otherwise the already-saved logo (fetched from the FHIR Binary, which
 * requires auth — so we download it and render via an object URL).
 *
 * Shared by the lab and radiology facility editors.
 */
export function FacilityLogoField({ value, onChange, existingLogoRef }: FacilityLogoFieldProps) {
  const medplum = useMedplum()
  const [existingLogoUrl, setExistingLogoUrl] = useState<string | null>(null)
  const [pendingUrl, setPendingUrl] = useState<string | null>(null)

  // Load the already-saved logo. The FHIR Binary needs an auth token, so a plain
  // <img src> won't work — download the blob and render it via an object URL.
  useEffect(() => {
    if (!existingLogoRef) {
      setExistingLogoUrl(null)
      return
    }
    // Clear any prior (now-revoked) object URL so a ref change doesn't briefly
    // render a stale/broken image while the new logo downloads.
    setExistingLogoUrl(null)
    let objectUrl: string | null = null
    let cancelled = false
    medplum
      .download(existingLogoRef)
      .then((blob) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setExistingLogoUrl(objectUrl)
      })
      .catch((err) => logError('super', 'Failed to load facility logo', err))
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [medplum, existingLogoRef])

  // Preview the freshly selected file (before it's saved).
  useEffect(() => {
    if (!value) {
      setPendingUrl(null)
      return
    }
    const url = URL.createObjectURL(value)
    setPendingUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [value])

  const previewUrl = pendingUrl ?? existingLogoUrl

  return (
    <Stack gap="xs">
      <FileInput
        label="Logo"
        placeholder="Upload facility logo"
        accept="image/png,image/jpeg,image/webp"
        leftSection={<IconUpload size={16} />}
        value={value}
        onChange={onChange}
      />
      {previewUrl && (
        <Stack gap={4} align="flex-start">
          <Text size="xs" c="dimmed">
            {pendingUrl ? 'New logo preview' : 'Current logo'}
          </Text>
          <Image src={previewUrl} alt="Facility logo" h={60} w="auto" fit="contain" />
        </Stack>
      )}
    </Stack>
  )
}
